import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Instagram, ChevronRight, Truck } from 'lucide-react';
import { ROUTES } from '@/lib/index';

const navLinks = [
  { label: 'INICIO', path: ROUTES.HOME },
  { label: 'CATÁLOGO', path: ROUTES.CATALOG },
  { label: 'PRECIOS', path: ROUTES.PRICING },
  { label: 'NUESTROS CLIENTES', path: ROUTES.CLIENTS },
  { label: 'HACER PEDIDO', path: ROUTES.ORDER },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top Banner */}
      <div className="bg-primary text-primary-foreground text-center py-2 text-xs font-bold tracking-widest flex items-center justify-center gap-2">
        <Truck size={14} />
        ENVÍOS A TODA LA REPÚBLICA MEXICANA
        <Truck size={14} />
      </div>

      {/* Navbar */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background/95 border-b border-border shadow-lg' : 'bg-background'
        }`}
      >
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to={ROUTES.HOME} className="flex items-center gap-3 group">
            <div className="relative">
              <span
                className="text-4xl font-black text-primary leading-none"
                style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '-2px' }}
              >
                23
              </span>
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-sm font-black text-foreground tracking-widest uppercase">DOS DE TRES</span>
              <span className="text-[10px] text-muted-foreground tracking-widest uppercase">Diseñado para competir.</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              const isOrder = link.path === ROUTES.ORDER;
              if (isOrder) {
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="ml-4 px-5 py-2 bg-primary text-primary-foreground font-bold text-xs tracking-widest rounded hover:bg-primary/90 transition-all duration-200 flex items-center gap-1"
                  >
                    {link.label}
                    <ChevronRight size={12} />
                  </Link>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 text-xs font-bold tracking-widest transition-colors duration-200 rounded ${
                    isActive
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Social Icons */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://wa.me/528333007124"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors font-mono"
            >
              <Phone size={14} />
              833 300 7124
            </a>
            <a
              href="https://instagram.com/23_dosdetres"
              target="_blank"
              rel="noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              <Instagram size={18} />
            </a>
          </div>

          {/* Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden text-foreground p-2"
            aria-label="Abrir menú"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-border bg-card"
            >
              <div className="container mx-auto px-4 py-4 flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`py-3 px-4 text-sm font-bold tracking-widest rounded transition-colors ${
                      location.pathname === link.path
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
                  <a
                    href="https://wa.me/528333007124"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary font-mono"
                  >
                    <Phone size={16} /> 833 300 7124
                  </a>
                  <a
                    href="https://instagram.com/23_dosdetres"
                    target="_blank"
                    rel="noreferrer"
                    className="text-muted-foreground hover:text-primary"
                  >
                    <Instagram size={20} />
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-card border-t border-border mt-16">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl font-black text-primary" style={{ letterSpacing: '-2px' }}>23</span>
                <div>
                  <p className="text-sm font-black tracking-widest">DOS DE TRES</p>
                  <p className="text-[10px] text-muted-foreground tracking-widest">Diseñado para competir.</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Uniformes personalizados de alta calidad para todo tipo de deporte y profesión.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-bold tracking-widest text-primary mb-4 uppercase">Navegación</h4>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold tracking-widest text-primary mb-4 uppercase">Contacto</h4>
              <div className="space-y-3">
                <a
                  href="https://wa.me/528333007124"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Phone size={16} />
                  <span className="font-mono">833 300 7124</span>
                </a>
                <a
                  href="https://instagram.com/23_dosdetres"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Instagram size={16} />
                  @23_dosdetres
                </a>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Truck size={16} />
                  Envíos a toda la República
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-[10px] text-muted-foreground tracking-widest font-bold uppercase mb-2">Programas</p>
                <div className="flex flex-wrap gap-2">
                  {['Patrocinios', 'Comisiones', 'Cash Back'].map((p) => (
                    <span key={p} className="text-[10px] px-2 py-1 rounded border border-primary/30 text-primary font-bold tracking-wider">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-border text-center text-xs text-muted-foreground tracking-widest">
            © 2026 DOS DE TRES. TODOS LOS DERECHOS RESERVADOS.
          </div>
        </div>
      </footer>

      {/* WhatsApp Float */}
      <a
        href="https://wa.me/528333007124?text=Hola,%20quiero%20información%20sobre%20uniformes"
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-400 rounded-full flex items-center justify-center shadow-2xl transition-all duration-200 hover:scale-110 group"
        aria-label="WhatsApp"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>
    </div>
  );
}
